import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { fetchProjectIElements } from "@store/i-elements/i-elements-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCallback, useEffect, useState } from "react";
import { markupsSelector } from "@store/markups/markups-selector";
import { Markup } from "@custom-types/project-markups-types";
import { hasFetchedMarkupsIElementsSelector } from "@store/sdb-i-elements/sdb-i-elements-selectors";
import {
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";

interface UseProjectMarkupsReturn {
  /** The array of fetched markup entities */
  markups: Markup[];

  /** Whether the project markups are being fetched from the backend */
  isFetchingMarkups: boolean;
}

/**
 * Custom hook that takes care of fetching all markup elements and other associated elements
 *
 * Since a project can have thousands of elements, fetching all of them is inefficient.
 * We will fetch only the elements we need to keep the store as lightweight as possible.
 */
export function useProjectMarkups({
  projectId,
}: BaseProjectIdProps): UseProjectMarkupsReturn {
  const dispatch = useAppDispatch();
  const projectApiClient = useProjectApiClient({
    projectId,
  });
  const markups = useAppSelector(markupsSelector);
  const hasFetchedMarkupsIElements = useAppSelector(
    hasFetchedMarkupsIElementsSelector
  );

  const [isFetchingMarkups, setIsFetchingMarkups] = useState<boolean>(false);

  const fetchMarkupTemplates = useCallback(async (): Promise<IElement[]> => {
    const templatesContainer = await projectApiClient.getAllIElements({
      type: IElementType.group,
      typeHints: [IElementTypeHint.markup],
    });

    const ancestorIds = templatesContainer.map((element) => element.id);
    const templates = await projectApiClient.getAllIElements({
      ancestorIds,
    });

    return templates;
  }, [projectApiClient]);

  // Fetches the ielements required to display the annotations table. The required ielements are:
  // - Markup ielements
  // - Direct children ielements of each markup ielement:
  //    - Assignee ielement
  //    - Due date ielement
  //    - Status ielement
  //    - Thumbnail ielement
  //    - Attachments group ielement. This ielement is the parent of the annotation attachments and it
  //      can be used to count the amount of attachments.
  // - Markup templates ielements. These are needed to edit the annotations.
  // - Root ielement. It is always required to have it in the redux store when handling ielements.
  useEffect(() => {
    async function fetchMarkups(): Promise<void> {
      setIsFetchingMarkups(true);

      await dispatch(
        fetchProjectIElements({
          // We specify the markup type so that in the fulfilled case of the thunk
          // we can set the hasFetchedMarkupsIElements flag in the store.
          types: [IElementType.markup],
          fetcher: async () => {
            const markups = await projectApiClient.getAllIElements({
              type: IElementType.markup,
            });

            if (!markups.length) {
              return [];
            }

            const markupIds = markups.map((markup) => markup.id);

            const [children, templates, root] = await Promise.all([
              projectApiClient.getAllIElements({
                ancestorIds: markupIds,
                typeHints: [
                  IElementTypeHint.markupAssigneeId,
                  IElementTypeHint.markupIssueDueDate,
                  IElementTypeHint.markupIssueStatus,
                  IElementTypeHint.markupIssueImage,
                  IElementTypeHint.attachments,
                ],
              }),
              fetchMarkupTemplates(),
              projectApiClient.getRootIElement(),
            ]);

            return [...markups, ...children, ...templates, root];
          },
        })
      );

      setIsFetchingMarkups(false);
    }

    if (!hasFetchedMarkupsIElements) {
      void fetchMarkups();
    }
  }, [
    dispatch,
    fetchMarkupTemplates,
    hasFetchedMarkupsIElements,
    projectApiClient,
  ]);

  return {
    markups,
    isFetchingMarkups,
  };
}
